// A apresentação muda com a largura; os destinos continuam filtrados por RBAC.
// Fase 17.8, Plano 09 (D-16, U-09): cabeçalho de 56px, itens de 40px, rótulo de grupo só como aria-label e menu do avatar no rodapé.
// Plano 10 (D-17): indicador do item ativo que desliza, flyout acessível no rail, submenus com animação de altura e tooltips
// com o nome completo (rail e rótulos cortados por reticências).
// Plano 13 (D-15): badges de pendência por módulo (soma no grupo, número no subitem, ponto no rail) vindos de GET /painel/pendencias.
// Plano 15 (D-18): o campo de busca de página dá lugar ao botão "Buscar" (Ctrl K) que abre a paleta de comandos.
import { useEffect, useId, useState, type ReactElement } from 'react';
import {
  Activity, BarChart3, Boxes, Calendar, CheckCircle, ChevronRight, ClipboardList,
  CircleHelp, Factory, FolderTree, LayoutDashboard, RefreshCw, Search, Shield, ShoppingCart, Truck,
} from 'lucide-react';
import type { SessaoAtualResponse } from '@pieltec/contracts';
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar,
} from '../ui/sidebar';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { usePendenciasSidebar, type EstadoPendencias } from '../../hooks/use-pendencias-sidebar';
import { ehMac } from '../../lib/busca/paleta';
import { chaveGrupo, chaveItem, rotuloComPendencia } from '../../lib/painel/pendencias';
import { FlyoutSubmenu } from './flyout-submenu';
import { IndicadorAtivoMenu } from './indicador-ativo-menu';
import { MenuAvatar } from './menu-avatar';
import { SidebarBadge } from './sidebar-badge';
import { filtrarNavegacaoPorPermissoes, resolverDestinoNavegacao, type GrupoNavegacao } from './navegacao';

export interface AppSidebarProps {
  readonly usuario: SessaoAtualResponse | null;
  readonly pathname: string;
  /** Abre a paleta Ctrl+K (o estado da paleta vive no AppShell). */
  readonly onAbrirBusca?: () => void;
  /** Abre a folha de atalhos (item do menu do avatar). */
  readonly onAbrirAtalhos?: () => void;
}

const ICONS = {
  CircleHelp,
  LayoutDashboard, ClipboardList, Truck, Factory, Calendar, CheckCircle, Boxes,
  ShoppingCart, Activity, BarChart3, FolderTree, CloudSync: RefreshCw, Shield,
};
function renderIcon(nome: string): ReactElement {
  const Icon = Object.entries(ICONS).find(([key]) => key === nome)?.[1] ?? LayoutDashboard;
  return <Icon className="size-4 shrink-0" aria-hidden="true" />;
}

/** Tooltip com o nome completo: sempre no rail; no menu expandido só quando o rótulo está cortado por reticências. */
function ComTooltipDoRotulo({ rotulo, noRail, children }: {
  readonly rotulo: string;
  readonly noRail: boolean;
  readonly children: ReactElement;
}): ReactElement {
  const [aberto, setAberto] = useState(false);
  const [cortado, setCortado] = useState(false);
  const medir = (elemento: HTMLElement): void => {
    const texto = elemento.querySelector<HTMLElement>('.sidebar-rotulo');
    setCortado(texto !== null && texto.scrollWidth > texto.clientWidth + 1);
  };
  return (
    <Tooltip open={aberto && (noRail || cortado)} onOpenChange={setAberto}>
      <TooltipTrigger asChild onPointerEnter={(evento) => medir(evento.currentTarget)} onFocus={(evento) => medir(evento.currentTarget)}>
        {children}
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} data-tooltip-menu>{rotulo}</TooltipContent>
    </Tooltip>
  );
}

function GrupoComItens({ grupo, pathname, pendencias }: {
  readonly grupo: GrupoNavegacao;
  readonly pathname: string;
  readonly pendencias: EstadoPendencias;
}): ReactElement {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const destinoAtivo = resolverDestinoNavegacao(pathname, [grupo]);
  const grupoAtivo = destinoAtivo !== undefined;
  const [preferencia, setPreferencia] = useState<{ rota: string; aberto: boolean } | null>(null);
  const [flyoutAberto, setFlyoutAberto] = useState(false);
  const idSubmenu = useId();
  // Navegar para outra área revela seu grupo, sem impedir o recolhimento manual.
  const aberto = preferencia?.rota === pathname ? preferencia.aberto : grupoAtivo;
  const fecharDrawer = () => { if (isMobile) setOpenMobile(false); };
  const noRail = !isMobile && state === 'collapsed';
  const contagemGrupo = pendencias.pendencias.grupos[grupo.id] ?? 0;

  if (noRail && grupo.itens) {
    return (
      <FlyoutSubmenu
        grupo={grupo} pathname={pathname} aberto={flyoutAberto} onAbertoChange={setFlyoutAberto}
        icone={renderIcon(grupo.iconeNome)}
        contagemGrupo={contagemGrupo}
        contagens={pendencias.pendencias.itens}
        pulsos={pendencias.pulsos}
      />
    );
  }

  return (
    <SidebarMenuItem>
      <ComTooltipDoRotulo rotulo={rotuloComPendencia(grupo.rotulo, contagemGrupo)} noRail={false}>
        <SidebarMenuButton
          type="button" isActive={grupoAtivo} data-testid={`sidebar-group-${grupo.id}`}
          aria-expanded={aberto} aria-controls={idSubmenu} data-state={aberto ? 'open' : 'closed'}
          onClick={() => setPreferencia({ rota: pathname, aberto: !aberto })}
        >
          {renderIcon(grupo.iconeNome)}<span className="sidebar-rotulo">{grupo.rotulo}</span>
          <SidebarBadge modulo={grupo.id} contagem={contagemGrupo} pulsar={pendencias.pulsos.has(chaveGrupo(grupo.id))} />
          <ChevronRight aria-hidden="true" className={contagemGrupo > 0 ? 'sidebar-chevron' : 'sidebar-chevron ml-auto'} />
        </SidebarMenuButton>
      </ComTooltipDoRotulo>
      {/* Altura animada por grid-template-rows (shell-premium.css, exceção submenu-altura); fechado fica invisível e fora do foco. */}
      <div id={idSubmenu} className="submenu-animado" data-state={aberto ? 'open' : 'closed'}>
        <div className="submenu-animado__interno">
          <SidebarMenuSub>
            {grupo.itens?.map((item) => {
              const ativo = destinoAtivo?.item?.id === item.id;
              const contagemItem = pendencias.pendencias.itens[item.id] ?? 0;
              return (
                <SidebarMenuSubItem key={item.id}>
                  <ComTooltipDoRotulo rotulo={rotuloComPendencia(item.rotulo, contagemItem)} noRail={false}>
                    <SidebarMenuSubButton asChild isActive={ativo} data-testid={`sidebar-subitem-${item.id}`}>
                      <a href={item.caminho} onClick={fecharDrawer} aria-current={ativo ? 'page' : undefined}>
                        <span className="sidebar-rotulo">{item.rotulo}</span>
                        <SidebarBadge modulo={item.id} contagem={contagemItem} pulsar={pendencias.pulsos.has(chaveItem(item.id))} />
                        {ativo && <span className="sr-only">, página atual</span>}
                      </a>
                    </SidebarMenuSubButton>
                  </ComTooltipDoRotulo>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </div>
      </div>
    </SidebarMenuItem>
  );
}

/** Rótulo do atalho da paleta: "Ctrl K", ou "⌘ K" no Mac (descoberto depois da montagem, sem divergir do servidor). */
function useAtalhoDaBusca(): string {
  const [mac, setMac] = useState(false);
  useEffect(() => {
    setMac(ehMac(navigator));
  }, []);
  return mac ? '⌘ K' : 'Ctrl K';
}

export function AppSidebar({ usuario, pathname, onAbrirBusca, onAbrirAtalhos }: AppSidebarProps): ReactElement {
  const { isMobile, setOpenMobile, state } = useSidebar();
  const atalhoDaBusca = useAtalhoDaBusca();
  const grupos = filtrarNavegacaoPorPermissoes(usuario?.permissions ?? []);
  // Badges de pendência (D-15): o hook chama carregarPendencias (lib/painel/pendencias) a cada navegação, evento ao vivo e recarga
  // de 2 min; falha nunca bloqueia o menu.
  const pendencias = usePendenciasSidebar({ ativo: usuario !== null, chaveSessao: usuario?.userId, pathname });
  const fecharDrawer = () => { if (isMobile) setOpenMobile(false); };
  const noRail = !isMobile && state === 'collapsed';
  // Índice do item de nível 1 ativo (o indicador desliza até ele).
  const indiceAtivo = grupos.findIndex((grupo) => (grupo.itens?.length ? resolverDestinoNavegacao(pathname, [grupo]) !== undefined : pathname === grupo.caminho));
  const comIndicador = indiceAtivo >= 0;
  return (
    <TooltipProvider delayDuration={300}>
      <div data-theme="mineral-nav" className="flex shrink-0 bg-surface text-foreground">
        <Sidebar collapsible="icon" className="border-r border-border bg-surface text-foreground">
          <SidebarHeader className="shell-sidebar-cabecalho">
            <a href="/painel" data-testid="wordmark-link" aria-label="Pieltec — Painel" onClick={fecharDrawer}
              className="shell-sidebar-marca flex w-full items-center">
              {/* Símbolo recortado da logo (sem editar a imagem) + nome em texto; o rótulo do link anuncia uma vez só. */}
              <span className="sidebar-brand" aria-hidden="true">
                <span className="sidebar-brand__simbolo" data-testid="sidebar-brand-simbolo">
                  <img src="/brand/logo-branco-recorte.png" alt="" className="sidebar-brand__imagem" />
                </span>
                <span className="sidebar-brand__nome" data-testid="sidebar-brand-nome">PIELTEC</span>
              </span>
            </a>
          </SidebarHeader>
          <SidebarContent>
            <div className="shell-sidebar-busca">
              <ComTooltipDoRotulo rotulo={`Buscar (${atalhoDaBusca})`} noRail={noRail}>
                <button
                  type="button" className="shell-sidebar-busca__botao" data-testid="sidebar-buscar"
                  aria-label={`Buscar (${atalhoDaBusca})`} aria-haspopup="dialog"
                  onClick={() => { fecharDrawer(); onAbrirBusca?.(); }}
                >
                  <Search className="size-4 shrink-0" aria-hidden="true" />
                  <span className="sidebar-rotulo">Buscar</span>
                  <kbd className="shell-sidebar-busca__atalho" aria-hidden="true">{atalhoDaBusca}</kbd>
                </button>
              </ComTooltipDoRotulo>
            </div>
            <ScrollArea className="h-full">
              <SidebarGroup role="group" aria-label="Área de trabalho">
                <SidebarGroupContent>
                  <SidebarMenu className="sidebar-nivel1" data-com-indicador={comIndicador ? 'true' : undefined}>
                    <IndicadorAtivoMenu indiceAtivo={indiceAtivo} />
                    {grupos.map((grupo) => grupo.itens?.length ? (
                      <GrupoComItens key={grupo.id} grupo={grupo} pathname={pathname} pendencias={pendencias} />
                    ) : (
                      <SidebarMenuItem key={grupo.id}>
                        <ComTooltipDoRotulo rotulo={rotuloComPendencia(grupo.rotulo, pendencias.pendencias.grupos[grupo.id] ?? 0)} noRail={noRail}>
                          <SidebarMenuButton asChild isActive={pathname === grupo.caminho} data-testid={`sidebar-item-${grupo.id}`}>
                            <a href={grupo.caminho} onClick={fecharDrawer} aria-current={pathname === grupo.caminho ? 'page' : undefined}>
                              {renderIcon(grupo.iconeNome)}<span className="sidebar-rotulo">{grupo.rotulo}</span>
                              <SidebarBadge
                                modulo={grupo.id} contagem={pendencias.pendencias.grupos[grupo.id] ?? 0} variante={noRail ? 'ponto' : 'numero'}
                                pulsar={pendencias.pulsos.has(chaveGrupo(grupo.id))}
                              />
                              {pathname === grupo.caminho && <span className="sr-only">, página atual</span>}
                            </a>
                          </SidebarMenuButton>
                        </ComTooltipDoRotulo>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </ScrollArea>
          </SidebarContent>
          <SidebarFooter className="shell-sidebar-rodape">
            <MenuAvatar usuario={usuario} onAbrirAtalhos={onAbrirAtalhos} />
          </SidebarFooter>
        </Sidebar>
      </div>
    </TooltipProvider>
  );
}
